// The Invoices tab's list: what's owed, what's overdue, and every invoice.
import { useState } from 'react';
import AiTag from './AiTag';
import { Button, Input, Stat, StatRow } from '../lib/ui';
import { invoiceState, invoiceStats, formatDay, formatGBP, relativeDay, ymd } from '../lib/trade';
import { InvoicePill } from './TradeCommon';

const FILTERS = [
  { key: 'all',     label: 'All',     test: () => true },
  { key: 'unpaid',  label: 'Unpaid',  test: (s) => s === 'sent' || s === 'overdue' },
  { key: 'overdue', label: 'Overdue', test: (s) => s === 'overdue' },
  { key: 'paid',    label: 'Paid',    test: (s) => s === 'paid' },
  { key: 'draft',   label: 'Drafts',  test: (s) => s === 'draft' },
  { key: 'void',    label: 'Void',    test: (s) => s === 'void' },
];

export default function TradeInvoiceList({ trade, today, onOpen, onNew }) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const { invoices } = trade;
  const stats = invoiceStats(invoices, today);

  const q = search.trim().toLowerCase();
  const withState = invoices.map(inv => ({ inv, state: invoiceState(inv, today) }));
  const counts = Object.fromEntries(FILTERS.map(f => [f.key, withState.filter(x => f.test(x.state)).length]));
  const test = FILTERS.find(f => f.key === filter).test;
  const rows = withState
    .filter(x => test(x.state))
    .filter(x => !q || [x.inv.number, x.inv.billTo?.businessName].some(v => (v || '').toLowerCase().includes(q)))
    .sort((a, b) => ymd(b.inv.issueDate).localeCompare(ymd(a.inv.issueDate)) || b.inv.seq - a.inv.seq);

  if (!invoices.length) {
    return (
      <div className="trade-empty">
        <strong>No invoices yet</strong>
        Make one here, or from a business on the Trade tab. Prices come from your trade price list.
        <div style={{ marginTop: 14 }}><Button variant="primary" onClick={onNew}>+ New invoice</Button></div>
      </div>
    );
  }

  return (
    <div>
      <StatRow>
        <Stat label="Unpaid" value={formatGBP(stats.unpaidTotal)} sub={`${stats.unpaidCount} invoice${stats.unpaidCount === 1 ? '' : 's'} sent`} accent="var(--info-fg)" />
        <Stat
          label="Overdue"
          value={formatGBP(stats.overdueTotal)}
          sub={stats.overdueCount ? `${stats.overdueCount} past the due date` : 'nothing overdue'}
          accent={stats.overdueCount ? 'var(--error)' : undefined}
          valueColor={stats.overdueCount ? 'var(--bad-fg)' : undefined}
        />
        <Stat label="Paid this month" value={formatGBP(stats.paidThisMonth)} accent="var(--brand-2)" />
        <Stat label="Drafts" value={stats.draftCount} sub="not sent yet" />
      </StatRow>

      <div className="trade-toolbar">
        <div className="trade-seg">
          {FILTERS.map(f => (f.key === 'all' || counts[f.key] > 0) && (
            <button key={f.key} type="button" className={filter === f.key ? 'is-on' : ''} onClick={() => setFilter(f.key)}>
              {f.label}<span className="n">{counts[f.key]}</span>
            </button>
          ))}
        </div>
        <div className="trade-toolbar__end">
          <Input placeholder="Search number or business…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="ui-card" style={{ padding: 0, overflowX: 'auto' }}>
        <table className="trade-table">
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Business</th>
              <th>Due</th>
              <th className="num">Amount</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ inv, state }) => (
              <tr key={inv._id} onClick={() => onOpen(inv._id)}>
                <td><b>{inv.number}</b><AiTag record={inv} /><span className="sub">{formatDay(inv.issueDate)}</span></td>
                <td>{inv.billTo?.businessName}</td>
                <td>
                  {formatDay(inv.dueDate)}
                  {(state === 'sent' || state === 'overdue') && <span className="sub">{relativeDay(inv.dueDate, today)}</span>}
                  {state === 'paid' && inv.paidAt && <span className="sub">paid {formatDay(inv.paidAt)}</span>}
                </td>
                <td className="num"><b>{formatGBP(inv.total)}</b></td>
                <td><InvoicePill invoice={inv} today={today} /></td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 24 }}>No invoices match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
